"use client";

import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { useData } from "@/i18n/LangContext";

interface DemoVideoProps {
  src: string;
  poster?: string;
  title?: string;
  caption?: string;
  width?: number;
  height?: number;
  delay?: number;
}

export default function DemoVideo({
  src,
  poster,
  title,
  caption,
  width,
  height,
  delay = 0,
}: DemoVideoProps) {
  const { ui } = useData();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [started, setStarted] = useState(false);
  const heading = title || ui.workDetail.demoVideo;

  const isPortrait = !!(width && height) && height > width;
  // Feste Proportion reserviert die Höhe vorab → kein Layout-Sprung beim Laden
  const aspectRatio = width && height ? `${width} / ${height}` : undefined;

  const handlePlay = () => {
    setStarted(true);
    videoRef.current?.play();
  };

  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay }}
      data-cy="demo-video"
    >
      <h2 className="text-2xl md:text-3xl font-bold mb-6 text-[#ffcc88]">{heading}</h2>

      <div className="flex justify-center">
        <div
          className={`rounded-xl overflow-hidden border border-[rgba(255,204,136,0.15)] shadow-glow-orange-sm bg-black w-full ${
            isPortrait ? "max-w-lg" : "max-w-3xl"
          }`}
        >
          <div className="relative">
            <video
              ref={videoRef}
              src={src}
              poster={poster}
              controls={started}
              playsInline
              preload="metadata"
              style={aspectRatio ? { aspectRatio } : undefined}
              className="w-full block bg-black object-cover"
              onPlay={() => setStarted(true)}
            />

            {!started && (
              <button
                type="button"
                onClick={handlePlay}
                aria-label={ui.media.play}
                className="absolute inset-0 flex items-center justify-center group cursor-pointer"
              >
                <span className="absolute inset-0 bg-black/10 transition-colors duration-300 group-hover:bg-black/35" />
                <motion.span
                  className="relative flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-[#ffcc88] text-[#1E0B32] shadow-[0_0_35px_rgba(255,204,136,0.45)]"
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                >
                  <Play className="w-7 h-7 md:w-9 md:h-9 translate-x-0.5" fill="currentColor" strokeWidth={0} />
                </motion.span>
              </button>
            )}
          </div>

          {caption && (
            <p className="text-white/80 text-sm px-4 py-3 border-t border-[rgba(255,204,136,0.15)]">
              {caption}
            </p>
          )}
        </div>
      </div>
    </motion.section>
  );
}
